import type { ReactNode } from 'react';

/**
 * Presentational status banner shared by cloud onboarding
 * ({@link OnboardingBanner}) and the platform-map "Save & Rerun" result
 * ({@link SetupPlatformMap}). A status icon (spinner / check / fail) sits
 * beside a title + optional body, with an optional dismiss button.
 *
 * Pure markup — callers own the lifecycle (what to show, when to dismiss)
 * so a one-shot save result and a long-lived onboarding run can share one
 * look without sharing state.
 */
export type StatusKind = 'running' | 'success' | 'error';

export function StatusBanner({
  kind,
  title,
  body,
  onDismiss,
}: {
  kind: StatusKind;
  title: ReactNode;
  body?: ReactNode;
  /** When provided, a dismiss (×) button appears. */
  onDismiss?: () => void;
}) {
  return (
    <div className={`snap-onb-banner snap-onb-banner--${kind}`} role='status'>
      <div className='snap-onb-banner__icon'>
        {kind === 'running' ? <Spinner /> : kind === 'success' ? <CheckIcon /> : <FailIcon />}
      </div>
      <div className='snap-onb-banner__text'>
        <div className='snap-onb-banner__title'>{title}</div>
        {body ? <div className='snap-onb-banner__body'>{body}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type='button'
          className='snap-onb-banner__dismiss'
          onClick={onDismiss}
          aria-label='Dismiss'
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

function Spinner() {
  return (
    <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden>
      <circle
        cx='12'
        cy='12'
        r='10'
        fill='none'
        stroke='currentColor'
        strokeWidth='2.5'
        strokeDasharray='40 60'
        strokeLinecap='round'
      >
        <animateTransform
          attributeName='transform'
          type='rotate'
          from='0 12 12'
          to='360 12 12'
          dur='1s'
          repeatCount='indefinite'
        />
      </circle>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden>
      <path d='M5 12l5 5L20 7' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

function FailIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden>
      <circle cx='12' cy='12' r='10' fill='none' stroke='currentColor' strokeWidth='2' />
      <path d='M12 7v5M12 16v.5' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' />
    </svg>
  );
}
